/**
 * Versioned-document HTTP client (SPEC §1A.3): `GET/PUT /v1/docs/{type}/{id}` with `If-Match`,
 * 409 handling via `resolveConflict`, and `GET /v1/docs/changes?since=` for reconnect catch-up.
 * `fetchFn` is injectable so the conflict path is unit-tested without a browser.
 */
import { resolveConflict, type Doc, type DocType } from "./docs.ts";

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export type SaveResult<T> =
  | { kind: "saved"; doc: Doc<T> }
  | { kind: "keep_server"; server: Doc<T>; local: T }
  | { kind: "rejected"; server: Doc<T> };

type WireDoc = {
  doc_type: DocType;
  doc_id: string;
  version: number;
  body: unknown;
  form_factor: string | null;
};

function fromWire<T>(w: WireDoc): Doc<T> {
  return { docType: w.doc_type, docId: w.doc_id, version: w.version, body: w.body as T };
}

export class DocSync {
  private readonly fetchFn: FetchLike;

  constructor(fetchFn: FetchLike = (i, init) => fetch(i, init)) {
    this.fetchFn = fetchFn;
  }

  async load<T extends Record<string, unknown>>(docType: DocType, docId: string): Promise<Doc<T> | null> {
    const res = await this.fetchFn(`/v1/docs/${docType}/${encodeURIComponent(docId)}`, {
      credentials: "same-origin",
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`load ${docType}/${docId}: ${String(res.status)}`);
    return fromWire<T>((await res.json()) as WireDoc);
  }

  /**
   * Save `local`, which was edited from `base` at `baseVersion` (0 when creating). On 409 the
   * per-type strategy decides: field-merge and retry once, keep the server copy, or reject.
   */
  async save<T extends Record<string, unknown>>(
    docType: DocType,
    docId: string,
    base: T,
    baseVersion: number,
    local: T,
    opts: { formFactor?: string } = {},
  ): Promise<SaveResult<T>> {
    const attempt = async (body: T, ifMatch: number): Promise<Response> =>
      this.fetchFn(`/v1/docs/${docType}/${encodeURIComponent(docId)}`, {
        method: "PUT",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "If-Match": String(ifMatch) },
        body: JSON.stringify({
          body,
          ...(opts.formFactor !== undefined ? { form_factor: opts.formFactor } : {}),
        }),
      });

    let res = await attempt(local, baseVersion);
    if (res.status === 409) {
      const conflict = (await res.json()) as { current_version: number; current_body: T };
      const server: Doc<T> = {
        docType,
        docId,
        version: conflict.current_version,
        body: conflict.current_body,
      };
      const resolution = resolveConflict(docType, base, local, conflict.current_body);
      switch (resolution.kind) {
        case "rejected":
          return { kind: "rejected", server };
        case "keep_server":
          return { kind: "keep_server", server, local };
        case "retry":
          res = await attempt(resolution.body, conflict.current_version);
          if (res.status === 409) {
            // Lost the race twice: surface the server copy rather than looping.
            const again = (await res.json()) as { current_version: number; current_body: T };
            return {
              kind: "keep_server",
              server: { docType, docId, version: again.current_version, body: again.current_body },
              local,
            };
          }
      }
    }
    if (!res.ok) throw new Error(`save ${docType}/${docId}: ${String(res.status)}`);
    return { kind: "saved", doc: fromWire<T>((await res.json()) as WireDoc) };
  }

  async changesSince(version: number): Promise<Doc[]> {
    const res = await this.fetchFn(`/v1/docs/changes?since=${String(version)}`, {
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error(`changes: ${String(res.status)}`);
    return ((await res.json()) as WireDoc[]).map((w) => fromWire(w));
  }
}
